import { MCA_ENTITIES } from "./mcaEntities";

export const RENEWAL_WATCH_FIELDS = [
  "status",
  "code",
  "date",
  "client",
  "product",
  "provider",
  "productType",
  "consultants",
  "order",
  "debitNote",
  "period.start",
  "period.end",
  "renewalCount",
  "insurerNoticeDate",
  "confirmedAt",
  "rejection.date",
  "rejection.reason",
  "rejection.remark",
  "delivery.date",
  "delivery.remark",
  "receivedRenewal.date",
  "receivedRenewal.remark",
  "total",
  "costTotal",
  "currency",
  "insuredName",
  "lastPolicyNumber",
  "address",
  "description",
  "createdBy",
  "to",
  "discountAmount",
  "commissionRate",
  "internalRemark",
];

export const NOTIFICATION_ENTITIES = [
  { value: "debitNote", label: "Debit Note" },
  { value: "order", label: "Order" },
  { value: "client", label: "Client" },
  { value: "renewal", label: "Renewal" },
  { value: "quotation", label: "Quotation" },
];

export const NOTIFICATION_EVENTS = [
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
];

export const NOTIFICATION_CHANNELS = [
  { value: "EMAIL", label: "Email" },
  { value: "IN_APP", label: "In-App" },
];

export const channelLabel = (value) =>
  NOTIFICATION_CHANNELS.find((c) => c.value === value)?.label ?? value;

export const entityLabel = (value) =>
  NOTIFICATION_ENTITIES.find((e) => e.value === value)?.label ??
  (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

export const eventLabel = (value) => {
  const normalized = value === "FIELD_CHANGE" ? "UPDATE" : value;
  return (
    NOTIFICATION_EVENTS.find((e) => e.value === normalized)?.label ?? normalized
  );
};

export const NOTIFICATION_PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "important", label: "Important" },
  { value: "normal", label: "Normal" },
];

export const RECIPIENT_TYPES = [
  { value: "SPECIFIC_USERS", label: "Specific Users" },
  { value: "DOCUMENT_ROLE", label: "Document Role" },
  { value: "USER_ROLE", label: "User Role" },
];

export const DOCUMENT_ROLES = {
  debitNote: [
    { value: "consultants", label: "Debit note consultants" },
    { value: "clientConsultants", label: "Client's consultants" },
    { value: "orderConsultants", label: "Order's consultants" },
    { value: "client", label: "Debit note's client" },
    { value: "provider", label: "Insurer (provider)" },
    { value: "signedBy", label: "Signed by" },
    { value: "signedBy2", label: "Signed by (2)" },
    { value: "createdBy", label: "Created by" },
  ],
  order: [
    { value: "consultants", label: "Order consultants" },
    { value: "bidConsultants", label: "Bid consultants" },
    { value: "clientConsultants", label: "Client's consultants" },
    { value: "client", label: "Order's client" },
    { value: "signedBy", label: "Signed by" },
    { value: "signedBy2", label: "Signed by (2)" },
    { value: "createdBy", label: "Created by" },
  ],
  client: [
    { value: "client", label: "Client (this record)" },
    { value: "consultants", label: "Client consultants" },
    { value: "createdBy", label: "Created by" },
  ],
  quotation: [
    { value: "consultants", label: "Quotation consultants" },
    { value: "clientConsultants", label: "Client's consultants" },
    { value: "client", label: "Quotation's client" },
    { value: "signedBy", label: "Signed by" },
    { value: "createdBy", label: "Created by" },
  ],
  renewal: [
    { value: "consultants", label: "Renewal consultants" },
    { value: "clientConsultants", label: "Client's consultants" },
    { value: "orderConsultants", label: "Order's consultants" },
    { value: "client", label: "Renewal's client" },
    { value: "createdBy", label: "Created by" },
  ],
};

export const getDocumentRoleOptions = (entity) =>
  (DOCUMENT_ROLES[entity] ?? []).map((role) =>
    typeof role === "string" ? { value: role, label: role } : role
  );

export const defaultNotificationRule = () => ({
  name: "",
  cname: "",
  description: "",
  status: "INACTIVE",
  triggerConfig: {
    targetEntity: null,
    event: null,
    watchFields: [],
  },
  recipients: [],
  channels: [],
  template: {
    subject: "",
    body: "",
    cc: "",
    bcc: "",
    attachmentKeys: [],
    encrypt: false,
    passwordSubject: "",
    passwordBody: "",
  },
  debounceSeconds: 0,
});

export const SYSTEM_VARIABLE_LABELS = {
  now: "Current date/time",
  entityUrl: "Record link",
  entityLink: "Record link",
  "entity._id": "Record ID",
  entityId: "Record ID",
};

export const variableLabel = (name) => SYSTEM_VARIABLE_LABELS[name] ?? name;

export const DEFAULT_VARIABLES = [
  "entity.code",
  "entity._id",
  "entityUrl",
  "entity.status",
  "client.name",
  "product.name",
  "consultant.name",
  "field",
  "oldValue",
  "newValue",
  "changedBy.name",
  "now",
];

export const RECIPIENT_VARIABLES = [
  "recipient.staffId",
  "recipient.name",
  "recipient.cname",
  "recipient.chineseName",
  "recipient.aliasName",
  "recipient.surname",
  "recipient.givenName",
  "recipient.email",
  "recipient.personalEmail",
  "recipient.phone",
  "recipient.phone2",
  "recipient.workTitle",
  "recipient.department",
  "recipient.userType",
  "recipient.status",
];

const withRecipientVariables = (variables) => [
  ...variables,
  ...RECIPIENT_VARIABLES.filter((v) => !variables.includes(v)),
];

export const ENTITY_VARIABLES = {
  debitNote: withRecipientVariables([
    "entity.code",
    "entity._id",
    "entityUrl",
    "entity.status",
    "entity.paymentStatus",
    "entity.agingStatus",
    "entity.total",
    "entity.currency",
    "entity.policyNumber",
    "client.name",
    "client.email",
    "product.name",
    "provider.name",
    "provider.email",
    "consultant.name",
    "consultant.staffId",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  order: withRecipientVariables([
    "entity.code",
    "entity._id",
    "entityUrl",
    "entity.status",
    "client.name",
    "product.name",
    "provider.name",
    "consultant.name",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  client: withRecipientVariables([
    "entity.code",
    "entity._id",
    "entityUrl",
    "entity.name",
    "entity.email",
    "entity.rpqStatus",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  renewal: withRecipientVariables([
    "entity.code",
    "entity._id",
    "entityUrl",
    "entity.status",
    "client.name",
    "product.name",
    "consultant.name",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
};

export const getVariablesForEntity = (entity) =>
  ENTITY_VARIABLES[entity] ?? withRecipientVariables(DEFAULT_VARIABLES);

export const getWatchFieldsForEntity = (entity) => {
  if (!entity) return [];
  if (entity === "renewal") return RENEWAL_WATCH_FIELDS;
  const mcaEntity = MCA_ENTITIES.find((e) => e.value === entity);
  return mcaEntity?.watchFields ?? [];
};
